import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { badgeClass, dangerButtonClass, Field, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../ui/FormControls';
import { PasswordInput } from './UserForm';
import { cn } from '../../lib/utils';

interface Status {
  enabled: boolean;
  pending: boolean;
  recoveryCodesLeft: number;
}

interface Setup {
  secret: string;
  otpauthUrl: string;
  qrSvg: string;
}

const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';
const sixDigits = (value: string) => /^\d{6}$/.test(value.replace(/\s/g, ''));

const CodeInput: React.FC<{ id: string; value: string; onChange: (value: string) => void; error?: string }> = ({ id, value, onChange, error }) => (
  <input
    id={id}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    autoComplete="one-time-code"
    inputMode="numeric"
    maxLength={7}
    placeholder="123456"
    aria-invalid={!!error}
    className={cn(inputClass, 'font-mono tracking-widest', error && inputErrorClass)}
  />
);

// Recovery codes are shown once: the user copies, downloads or writes them down now
const RecoveryCodes: React.FC<{ codes: string[]; onDone: () => void }> = ({ codes, onDone }) => {
  const text = `RightFitERP recovery codes\nEach code works once, when the authenticator app is not at hand.\n\n${codes.join('\n')}\n`;
  const copy = () =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success('Recovery codes copied'))
      .catch(() => toast.error('Copying is blocked in this browser. Download them instead.'));
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'rightfiterp-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-3 p-4" data-testid="recovery-codes">
      <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">
        Keep these recovery codes somewhere safe. Each one signs you in once if your phone is lost. They are shown only now.
      </div>
      <ul className="grid grid-cols-2 gap-2 rounded-md bg-gray-50 p-3 font-mono text-sm text-gray-900 sm:grid-cols-5">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={copy} className={secondaryButtonClass}>
          Copy
        </button>
        <button type="button" onClick={download} className={secondaryButtonClass}>
          Download
        </button>
        <button type="button" onClick={onDone} className={primaryButtonClass}>
          I have saved them
        </button>
      </div>
    </div>
  );
};

// Two-step sign-in of the person signed in: set up with an authenticator app, new recovery codes, turning it off
const TwoFactorSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery<Status>({ queryKey: ['profile', 'two-factor'], queryFn: async () => (await api.get('/auth/2fa')).data });
  const [setup, setSetup] = useState<Setup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<'idle' | 'disable' | 'regenerate'>('idle');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ code?: string; password?: string }>({});

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['profile', 'two-factor'] });
    queryClient.invalidateQueries({ queryKey: ['profile', 'activities'] });
  };
  const reset = () => {
    setCode('');
    setPassword('');
    setError({});
  };
  const codeProblem = (failure: unknown, fallback: string) => {
    const message = getApiErrorMessage(failure, fallback);
    if (/password/i.test(message)) setError({ password: message });
    else setError({ code: message });
  };

  const start = useMutation({
    mutationFn: async () => (await api.post<Setup>('/auth/2fa/setup')).data,
    onSuccess: (data) => {
      reset();
      setSetup(data);
    },
    onError: (failure) => toast.error(getApiErrorMessage(failure, 'Two-step sign-in could not be set up')),
  });
  const enable = useMutation({
    mutationFn: async () => (await api.post<{ recoveryCodes: string[] }>('/auth/2fa/enable', { code: code.replace(/\s/g, '') })).data,
    onSuccess: (data) => {
      setSetup(null);
      reset();
      setCodes(data.recoveryCodes);
      refresh();
      toast.success('Two-step sign-in is on');
    },
    onError: (failure) => codeProblem(failure, 'The code could not be checked'),
  });
  const disable = useMutation({
    mutationFn: async () => (await api.post('/auth/2fa/disable', { password, code: code.replace(/\s/g, '') })).data,
    onSuccess: () => {
      setMode('idle');
      reset();
      refresh();
      toast.success('Two-step sign-in is off');
    },
    onError: (failure) => codeProblem(failure, 'Two-step sign-in could not be turned off'),
  });
  const regenerate = useMutation({
    mutationFn: async () => (await api.post<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', { code: code.replace(/\s/g, '') })).data,
    onSuccess: (data) => {
      setMode('idle');
      reset();
      setCodes(data.recoveryCodes);
      refresh();
    },
    onError: (failure) => codeProblem(failure, 'New recovery codes could not be made'),
  });

  const submit = (event: React.FormEvent, action: () => void, needsPassword = false) => {
    event.preventDefault();
    const found: { code?: string; password?: string } = {};
    if (needsPassword && !password) found.password = 'Enter your password';
    if (!sixDigits(code)) found.code = 'Enter the six digits the authenticator app shows';
    setError(found);
    if (!found.code && !found.password) action();
  };

  const busy = start.isPending || enable.isPending || disable.isPending || regenerate.isPending;

  return (
    <section className={cardClass} aria-label="Two-step sign-in">
      <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
        <div className="rounded-lg bg-indigo-50 p-2">
          <DevicePhoneMobileIcon className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-900">Two-step sign-in</h2>
          <p className="mt-0.5 text-xs text-gray-500">After the password, a code from an authenticator app on your phone (Google Authenticator, Microsoft Authenticator and the like).</p>
        </div>
        {status && (
          <span className={cn(badgeClass, status.enabled ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}>{status.enabled ? 'On' : 'Off'}</span>
        )}
      </div>

      {isLoading || !status ? (
        <div className="p-4">
          <div className="h-10 animate-pulse rounded bg-gray-100" />
        </div>
      ) : codes ? (
        <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
      ) : setup ? (
        <form onSubmit={(event) => submit(event, () => enable.mutate())} noValidate className="space-y-4 p-4">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
            <li>In the authenticator app, add an account and scan this QR code.</li>
            <li>Enter the six-digit code the app then shows, to confirm it works.</li>
          </ol>
          <div className="flex flex-col items-start gap-4 sm:flex-row">
            <div className="h-44 w-44 shrink-0 rounded-md bg-white p-2 ring-1 ring-gray-200 [&>svg]:h-full [&>svg]:w-full" data-testid="two-factor-qr" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} />
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-500">Cannot scan? Type this key into the app instead:</p>
                <p className="mt-1 break-all font-mono text-sm text-gray-900" data-testid="two-factor-secret">
                  {setup.secret}
                </p>
              </div>
              <Field label="Code from the app" htmlFor="two-factor-enable-code" required error={error.code}>
                <CodeInput id="two-factor-enable-code" value={code} onChange={(value) => { setCode(value); setError({}); }} error={error.code} />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
            <button type="button" onClick={() => { setSetup(null); reset(); }} disabled={busy} className={secondaryButtonClass}>
              Cancel
            </button>
            <button type="submit" disabled={busy} className={primaryButtonClass}>
              {enable.isPending ? 'Checking…' : 'Turn on'}
            </button>
          </div>
        </form>
      ) : !status.enabled ? (
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">Off. Anyone with your password can sign in as you.</p>
          <button type="button" onClick={() => start.mutate()} disabled={busy} className={primaryButtonClass}>
            {start.isPending ? 'Preparing…' : 'Set up two-step sign-in'}
          </button>
        </div>
      ) : mode === 'idle' ? (
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className={cn('text-sm', status.recoveryCodesLeft <= 2 ? 'text-amber-700' : 'text-gray-600')}>
            On. {status.recoveryCodesLeft} of 10 recovery codes left{status.recoveryCodesLeft <= 2 ? ': make new ones soon.' : '.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => { reset(); setMode('regenerate'); }} className={secondaryButtonClass}>
              New recovery codes
            </button>
            <button type="button" onClick={() => { reset(); setMode('disable'); }} className={dangerButtonClass}>
              Turn off
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={(event) => submit(event, () => (mode === 'disable' ? disable.mutate() : regenerate.mutate()), mode === 'disable')} noValidate className="space-y-4 p-4">
          <p className="text-sm text-gray-700">
            {mode === 'disable'
              ? 'To turn two-step sign-in off, confirm your password and a code from the app.'
              : 'Enter a code from the app. New recovery codes replace the old ones, which stop working.'}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {mode === 'disable' && (
              <Field label="Password" htmlFor="two-factor-password" required error={error.password}>
                <PasswordInput id="two-factor-password" value={password} onChange={(value) => { setPassword(value); setError({}); }} invalid={!!error.password} autoComplete="current-password" />
              </Field>
            )}
            <Field label="Code from the app" htmlFor="two-factor-confirm-code" required error={error.code}>
              <CodeInput id="two-factor-confirm-code" value={code} onChange={(value) => { setCode(value); setError({}); }} error={error.code} />
            </Field>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
            <button type="button" onClick={() => { setMode('idle'); reset(); }} disabled={busy} className={secondaryButtonClass}>
              Cancel
            </button>
            <button type="submit" disabled={busy} className={mode === 'disable' ? dangerButtonClass : primaryButtonClass}>
              {busy ? 'Checking…' : mode === 'disable' ? 'Turn off' : 'Make new codes'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default TwoFactorSection;
